<template>
  <article class="article-detail iso-container" v-if="article">
    <header class="article-header">
      <span v-if="article.category" class="iso-tag">{{ article.category }}</span>
      <h1 class="article-title iso-glow-text">{{ article.title }}</h1>
      <div class="article-meta">
        <span v-if="article.published_at">{{ $t('article.publishedAt') }}: {{ article.published_at }}</span>
        <span v-if="article.source">{{ $t('article.source') }}: {{ article.source }}</span>
        <span v-if="article.view_count !== undefined">{{ $t('article.views') }}: {{ article.view_count }}</span>
      </div>
    </header>

    <section class="article-cover" v-if="article.cover_image">
      <img :src="article.cover_image" :alt="article.title" loading="lazy" />
    </section>

    <section class="article-content" v-html="article.content"></section>

    <aside class="article-related" v-if="related.length">
      <h2 class="iso-section-title">{{ $t('article.related') }}</h2>
      <div class="iso-grid">
        <IsometricCard
          v-for="item in related"
          :key="item.id"
          :id="item.id"
          :title="item.title"
          :summary="item.summary"
          :cover-image="item.cover_image"
          :date="item.published_at"
          :category="item.category"
          :slug="item.slug"
        />
      </div>
    </aside>
  </article>

  <div v-else-if="loading" class="iso-loading iso-container" style="padding-top:120px">{{ $t('common.loading') }}</div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { request } from '@/api'
import { useSeo } from '@/composables/useSeo'
import IsometricCard from '@/components/IsometricCard.vue'

interface Article {
  id: number
  title: string
  slug: string
  summary?: string
  content?: string
  category?: string
  cover_image?: string
  source?: string
  original_url?: string
  published_at?: string
  updated_at?: string
  view_count?: number
  tags?: string[]
}

const route = useRoute()

const article = ref<Article | null>(null)
const related = ref<Article[]>([])
const loading = ref(true)
let seoCleanup: (() => void) | null = null

async function fetchArticle() {
  const id = route.params.id as string
  if (!id) return
  loading.value = true
  try {
    const res = await request<{ data: Article }>(`/news/${id}`)
    article.value = res.data
    if (seoCleanup) seoCleanup()
    const { cleanup } = useSeo({
      id: article.value.id,
      title: article.value.title,
      summary: article.value.summary,
      cover_image: article.value.cover_image,
      published_at: article.value.published_at,
      updated_at: article.value.updated_at,
      source: article.value.source,
      category: article.value.category,
      tags: article.value.tags,
    })
    seoCleanup = cleanup
  } catch {
    article.value = null
  } finally {
    loading.value = false
  }
}

async function fetchRelated() {
  try {
    const res = await request<{ data: Article[] }>('/news?limit=4')
    const id = Number(route.params.id)
    related.value = (res.data || []).filter(a => a.id !== id).slice(0, 3)
  } catch {
    related.value = []
  }
}

watch(() => route.params.id, () => {
  fetchArticle()
  fetchRelated()
})

onMounted(() => {
  fetchArticle()
  fetchRelated()
})
</script>

<style scoped>
.article-detail {
  padding-top: calc(var(--header-height) + 32px);
  padding-bottom: 64px;
  max-width: 860px;
}

.article-header {
  margin-bottom: 32px;
}

.article-title {
  font-size: 32px;
  font-weight: 800;
  line-height: 1.3;
  margin: 16px 0;
}

.article-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 13px;
  color: var(--iso-text-dim);
}

.article-cover {
  margin-bottom: 32px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--iso-border);
}

.article-cover img {
  width: 100%;
  display: block;
}

.article-content {
  font-size: 16px;
  line-height: 1.8;
  color: var(--iso-text);
}

.article-content :deep(p) {
  margin-bottom: 16px;
}

.article-content :deep(h2) {
  font-size: 22px;
  font-weight: 700;
  color: #fff;
  margin: 32px 0 16px;
}

.article-content :deep(h3) {
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  margin: 24px 0 12px;
}

.article-content :deep(img) {
  max-width: 100%;
  border-radius: 8px;
  margin: 16px 0;
}

.article-content :deep(a) {
  color: var(--iso-cyan);
  text-decoration: underline;
}

.article-related {
  margin-top: 64px;
}

@media (max-width: 768px) {
  .article-title {
    font-size: 24px;
  }

  .article-content {
    font-size: 15px;
  }
}
</style>
